import React, { useCallback, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import AlertBanner from '../../components/common/AlertBanner/AlertBanner';
import Button from '../../components/common/Button/Button';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import DecoratedHeader from '../../components/common/DecoratedHeader/DecoratedHeader';
import Logo from '../../components/common/Logo/Logo';
import { AppFormSearchableSelect } from '../../components/common/appForms';
import { LeadQualificationService } from '../../services/LeadQualificationService';
import { unmaskPhone } from '../../utils/masks';
import heroBackgroundImage from '../../assets/images/quero-comecar-hero.jpg';
import {
  createInitialLeadQualificationForm,
  LEAD_QUALIFICATION_BUSY_ROUTINE_OPTIONS,
  LEAD_QUALIFICATION_BUSY_ROUTINE_QUESTION,
  LEAD_QUALIFICATION_CONSULTING_EXPECTATION_OPTIONS,
  LEAD_QUALIFICATION_CONSULTING_EXPECTATION_QUESTION,
  LEAD_QUALIFICATION_INJURY_LIMITATION_OPTIONS,
  LEAD_QUALIFICATION_INJURY_LIMITATION_QUESTION,
  LEAD_QUALIFICATION_INVESTMENT_INTEREST_OPTIONS,
  LEAD_QUALIFICATION_INVESTMENT_INTEREST_QUESTION,
  LEAD_QUALIFICATION_MAIN_GOAL_OPTIONS,
  LEAD_QUALIFICATION_MAIN_GOAL_QUESTION,
  LEAD_QUALIFICATION_START_TIME_OPTIONS,
  LEAD_QUALIFICATION_START_TIME_QUESTION,
  LEAD_QUALIFICATION_TRAINING_EXPERIENCE_OPTIONS,
  LEAD_QUALIFICATION_TRAINING_EXPERIENCE_QUESTION,
  LEAD_QUALIFICATION_TRAINING_FORMAT_OPTIONS,
  LEAD_QUALIFICATION_TRAINING_FORMAT_QUESTION,
  LEAD_QUALIFICATION_TRAINING_RELATIONSHIP_OPTIONS,
  LEAD_QUALIFICATION_TRAINING_RELATIONSHIP_QUESTION,
  LEAD_QUALIFICATION_WEEKLY_COMMITMENT_OPTIONS,
  LEAD_QUALIFICATION_WEEKLY_COMMITMENT_QUESTION,
} from '../Checkout/checkout.constants';
import type {
  CheckoutPersonalData,
  LeadQualificationBusyRoutineBehavior,
  LeadQualificationConsultingExpectation,
  LeadQualificationFormState,
  LeadQualificationInjuryLimitation,
  LeadQualificationInvestmentInterest,
  LeadQualificationMainGoal,
  LeadQualificationStartTimeExpectation,
  LeadQualificationTrainingExperience,
  LeadQualificationTrainingFormat,
  LeadQualificationTrainingRelationship,
  LeadQualificationWeeklyCommitmentDays,
} from '../Checkout/checkout.types';
import {
  resolveLeadQualificationPersonalFieldError,
  validateLeadQualificationForm,
} from '../Checkout/checkout.utils';
import CheckoutContainer from '../Checkout/components/CheckoutContainer';
import PersonalDataStep from '../Checkout/steps/PersonalDataStep';
import fupStyles from '../Fup/Fup.module.css';
import pageStyles from '../Checkout/CheckoutPage.module.css';
import onboardingHeroStyles from '../Landpage/components/onboardingHero/onboardingHero.module.css';
import styles from './LeadQualificationPage.module.css';

type FeedbackState = {
  message: string;
  variant: 'error' | 'success';
} | null;

const scrollToPageTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const COMPLETED_QUERY_PARAM = 'completed';
const WHATSAPP_URL = process.env.REACT_APP_LEAD_QUALIFICATION_WHATSAPP_URL
  ?? process.env.REACT_APP_LANDPAGE_SUPPORT_WHATSAPP_URL
  ?? 'https://api.whatsapp.com/send?phone=5521978890377';

const HERO_TIME_BADGE_LABEL = 'Leva apenas alguns minutos · Inscrição sujeita à avaliação de perfil';
const HERO_CTA_LABEL = 'Quero dar o próximo passo';

const FIELD_FOCUS_ORDER = [
  'fullName',
  'phone',
  'email',
  'investmentInterest',
  'startTimeExpectation',
  'mainGoal',
  'trainingRelationship',
  'trainingExperience',
  'busyRoutineBehavior',
  'weeklyCommitmentDays',
  'trainingFormat',
  'consultingExpectation',
  'injuryLimitation',
];

const FIELD_FOCUS_TARGETS: Record<string, string> = {
  fullName: 'checkout-full-name',
  phone: 'checkout-phone',
  email: 'checkout-email',
  investmentInterest: 'lead-investment-interest',
  startTimeExpectation: 'lead-start-time-expectation',
  mainGoal: 'lead-main-goal',
  trainingRelationship: 'lead-training-relationship',
  trainingExperience: 'lead-training-experience',
  busyRoutineBehavior: 'lead-busy-routine-behavior',
  weeklyCommitmentDays: 'lead-weekly-commitment-days',
  trainingFormat: 'lead-training-format',
  consultingExpectation: 'lead-consulting-expectation',
  injuryLimitation: 'lead-injury-limitation',
};

const stageVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? '-100%' : '100%',
    opacity: 0,
  }),
};

const focusFirstInvalidField = (formErrors: Record<string, string>) => {
  const invalidField = FIELD_FOCUS_ORDER.find((field) => Boolean(formErrors[field]));
  if (!invalidField) {
    return;
  }

  window.setTimeout(() => {
    const targetId = FIELD_FOCUS_TARGETS[invalidField];
    const element = document.getElementById(targetId);
    const focusable = element?.matches('input, button, textarea, select, [tabindex]')
      ? element
      : element?.querySelector<HTMLElement>('input, button, textarea, select, [tabindex]');

    focusable?.focus();
    (element ?? focusable)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 0);
};

const LeadQualificationPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isCompleted = searchParams.get(COMPLETED_QUERY_PARAM) === 'true';

  const [form, setForm] = useState<LeadQualificationFormState>(createInitialLeadQualificationForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState<FeedbackState>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [animationDirection, setAnimationDirection] = useState(1);
  const isSubmitLockedRef = useRef(false);
  const formRef = useRef(form);
  formRef.current = form;

  const updatePersonalField = useCallback(<K extends keyof CheckoutPersonalData>(
    field: K,
    value: CheckoutPersonalData[K],
  ) => {
    setForm((prev) => {
      const nextPersonal = { ...prev.personal, [field]: value };

      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: field === 'phone'
          ? ''
          : resolveLeadQualificationPersonalFieldError(field, nextPersonal),
      }));

      return {
        ...prev,
        personal: nextPersonal,
      };
    });
    setFeedback(null);
  }, []);

  const handlePersonalFieldBlur = useCallback((field: 'email' | 'cpf' | 'phone') => {
    if (field !== 'phone') {
      return;
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      phone: resolveLeadQualificationPersonalFieldError('phone', formRef.current.personal),
    }));
  }, []);

  const updateQuestion = <K extends keyof Omit<LeadQualificationFormState, 'personal'>>(
    field: K,
    value: LeadQualificationFormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
    setFeedback(null);
  };

  const handleSubmitRequest = () => {
    if (isSubmitLockedRef.current || isSubmitting) {
      return;
    }

    const formErrors = validateLeadQualificationForm(form);
    setErrors(formErrors);

    if (Object.keys(formErrors).some((key) => formErrors[key])) {
      focusFirstInvalidField(formErrors);
      scrollToPageTop();
      return;
    }

    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    if (isSubmitLockedRef.current || isSubmitting) {
      return;
    }

    isSubmitLockedRef.current = true;
    setIsSubmitting(true);

    try {
      await LeadQualificationService.submit({
        fullName: form.personal.fullName.trim(),
        email: form.personal.email.trim(),
        callingCode: form.personal.callingCode,
        phone: unmaskPhone(form.personal.phone),
        investmentInterest: form.investmentInterest!,
        startTimeExpectation: form.startTimeExpectation!,
        mainGoal: form.mainGoal!,
        trainingRelationship: form.trainingRelationship!,
        trainingExperience: form.trainingExperience!,
        busyRoutineBehavior: form.busyRoutineBehavior!,
        weeklyCommitmentDays: form.weeklyCommitmentDays!,
        trainingFormat: form.trainingFormat!,
        consultingExpectation: form.consultingExpectation!,
        injuryLimitation: form.injuryLimitation!,
      });

      setShowConfirmModal(false);

      const nextParams = new URLSearchParams();
      nextParams.set(COMPLETED_QUERY_PARAM, 'true');

      setAnimationDirection(1);
      setSearchParams(nextParams, { replace: true });
      setFeedback(null);
      setForm(createInitialLeadQualificationForm());
      setErrors({});
      scrollToPageTop();
    } catch (error) {
      isSubmitLockedRef.current = false;
      setShowConfirmModal(false);
      setFeedback({
        message: error instanceof Error ? error.message : 'Não foi possível enviar sua qualificação.',
        variant: 'error',
      });
      scrollToPageTop();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStart = () => {
    setAnimationDirection(1);
    setHasStarted(true);
    scrollToPageTop();
  };

  const handleWhatsAppClick = () => {
    window.open(WHATSAPP_URL, '_blank', 'noopener');
  };

  const renderForm = () => (
    <CheckoutContainer>
      <div className={styles.logoHeader}>
        <Logo className={styles.formLogo} />
        <DecoratedHeader
          className={styles.formTitle}
          title="Antes de liberar seu atendimento, responda algumas perguntas."
        />
        <p className={pageStyles.headerSubtitle}>Leva apenas alguns minutos.</p>
      </div>

      {feedback && (
        <AlertBanner
          message={feedback.message}
          variant={feedback.variant}
        />
      )}

      <div className={styles.centeredLayout}>
        <article className={`${pageStyles.checkoutCard} ${styles.centeredCard}`}>
          <h2 className={pageStyles.cardTitle}>Dados</h2>
          <PersonalDataStep
            data={form.personal}
            errors={errors}
            requiresCpf={false}
            showBirthDate={false}
            showCpf={false}
            emailAfterPhone
            phoneLabel="WhatsApp"
            disabled={isSubmitting}
            onFieldChange={updatePersonalField}
            onFieldBlur={handlePersonalFieldBlur}
          />

          <h2 className={`${pageStyles.cardTitle} ${styles.questionsTitle}`}>Perguntas rápidas</h2>

          <div className={pageStyles.formFields}>
            <AppFormSearchableSelect
              id="lead-investment-interest"
              label={LEAD_QUALIFICATION_INVESTMENT_INTEREST_QUESTION}
              required
              value={form.investmentInterest ?? ''}
              options={LEAD_QUALIFICATION_INVESTMENT_INTEREST_OPTIONS}
              placeholder="Selecione uma opção"
              noResultsText="Nenhum resultado encontrado"
              allowEmpty={false}
              error={errors.investmentInterest}
              disabled={isSubmitting}
              onChange={(event) => updateQuestion(
                'investmentInterest',
                event.target.value as LeadQualificationInvestmentInterest,
              )}
            />

            <AppFormSearchableSelect
              id="lead-start-time-expectation"
              label={LEAD_QUALIFICATION_START_TIME_QUESTION}
              required
              value={form.startTimeExpectation ?? ''}
              options={LEAD_QUALIFICATION_START_TIME_OPTIONS}
              placeholder="Selecione uma opção"
              noResultsText="Nenhum resultado encontrado"
              allowEmpty={false}
              error={errors.startTimeExpectation}
              disabled={isSubmitting}
              onChange={(event) => updateQuestion(
                'startTimeExpectation',
                event.target.value as LeadQualificationStartTimeExpectation,
              )}
            />

            <AppFormSearchableSelect
              id="lead-main-goal"
              label={LEAD_QUALIFICATION_MAIN_GOAL_QUESTION}
              required
              value={form.mainGoal ?? ''}
              options={LEAD_QUALIFICATION_MAIN_GOAL_OPTIONS}
              placeholder="Selecione uma opção"
              noResultsText="Nenhum resultado encontrado"
              allowEmpty={false}
              error={errors.mainGoal}
              disabled={isSubmitting}
              onChange={(event) => updateQuestion(
                'mainGoal',
                event.target.value as LeadQualificationMainGoal,
              )}
            />

            <AppFormSearchableSelect
              id="lead-training-relationship"
              label={LEAD_QUALIFICATION_TRAINING_RELATIONSHIP_QUESTION}
              required
              value={form.trainingRelationship ?? ''}
              options={LEAD_QUALIFICATION_TRAINING_RELATIONSHIP_OPTIONS}
              placeholder="Selecione uma opção"
              noResultsText="Nenhum resultado encontrado"
              allowEmpty={false}
              error={errors.trainingRelationship}
              disabled={isSubmitting}
              onChange={(event) => updateQuestion(
                'trainingRelationship',
                event.target.value as LeadQualificationTrainingRelationship,
              )}
            />

            <AppFormSearchableSelect
              id="lead-training-experience"
              label={LEAD_QUALIFICATION_TRAINING_EXPERIENCE_QUESTION}
              required
              value={form.trainingExperience ?? ''}
              options={LEAD_QUALIFICATION_TRAINING_EXPERIENCE_OPTIONS}
              placeholder="Selecione uma opção"
              noResultsText="Nenhum resultado encontrado"
              allowEmpty={false}
              error={errors.trainingExperience}
              disabled={isSubmitting}
              onChange={(event) => updateQuestion(
                'trainingExperience',
                event.target.value as LeadQualificationTrainingExperience,
              )}
            />

            <AppFormSearchableSelect
              id="lead-busy-routine-behavior"
              label={LEAD_QUALIFICATION_BUSY_ROUTINE_QUESTION}
              required
              value={form.busyRoutineBehavior ?? ''}
              options={LEAD_QUALIFICATION_BUSY_ROUTINE_OPTIONS}
              placeholder="Selecione uma opção"
              noResultsText="Nenhum resultado encontrado"
              allowEmpty={false}
              error={errors.busyRoutineBehavior}
              disabled={isSubmitting}
              onChange={(event) => updateQuestion(
                'busyRoutineBehavior',
                event.target.value as LeadQualificationBusyRoutineBehavior,
              )}
            />

            <AppFormSearchableSelect
              id="lead-weekly-commitment-days"
              label={LEAD_QUALIFICATION_WEEKLY_COMMITMENT_QUESTION}
              required
              value={form.weeklyCommitmentDays ?? ''}
              options={LEAD_QUALIFICATION_WEEKLY_COMMITMENT_OPTIONS}
              placeholder="Selecione uma opção"
              noResultsText="Nenhum resultado encontrado"
              allowEmpty={false}
              error={errors.weeklyCommitmentDays}
              disabled={isSubmitting}
              onChange={(event) => updateQuestion(
                'weeklyCommitmentDays',
                event.target.value as LeadQualificationWeeklyCommitmentDays,
              )}
            />

            <AppFormSearchableSelect
              id="lead-training-format"
              label={LEAD_QUALIFICATION_TRAINING_FORMAT_QUESTION}
              required
              value={form.trainingFormat ?? ''}
              options={LEAD_QUALIFICATION_TRAINING_FORMAT_OPTIONS}
              placeholder="Selecione uma opção"
              noResultsText="Nenhum resultado encontrado"
              allowEmpty={false}
              error={errors.trainingFormat}
              disabled={isSubmitting}
              onChange={(event) => updateQuestion(
                'trainingFormat',
                event.target.value as LeadQualificationTrainingFormat,
              )}
            />

            <AppFormSearchableSelect
              id="lead-consulting-expectation"
              label={LEAD_QUALIFICATION_CONSULTING_EXPECTATION_QUESTION}
              required
              value={form.consultingExpectation ?? ''}
              options={LEAD_QUALIFICATION_CONSULTING_EXPECTATION_OPTIONS}
              placeholder="Selecione uma opção"
              noResultsText="Nenhum resultado encontrado"
              allowEmpty={false}
              error={errors.consultingExpectation}
              disabled={isSubmitting}
              onChange={(event) => updateQuestion(
                'consultingExpectation',
                event.target.value as LeadQualificationConsultingExpectation,
              )}
            />

            <AppFormSearchableSelect
              id="lead-injury-limitation"
              label={LEAD_QUALIFICATION_INJURY_LIMITATION_QUESTION}
              required
              value={form.injuryLimitation ?? ''}
              options={LEAD_QUALIFICATION_INJURY_LIMITATION_OPTIONS}
              placeholder="Selecione uma opção"
              noResultsText="Nenhum resultado encontrado"
              allowEmpty={false}
              error={errors.injuryLimitation}
              disabled={isSubmitting}
              onChange={(event) => updateQuestion(
                'injuryLimitation',
                event.target.value as LeadQualificationInjuryLimitation,
              )}
            />
          </div>

          <div className={fupStyles.footerActions}>
            <Button
              type="button"
              variant="primary"
              size="app"
              fullWidth
              iconRight="arrow_forward"
              onClick={() => {
                void handleSubmitRequest();
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Enviando...' : 'Enviar resposta'}
            </Button>
          </div>
        </article>
      </div>
    </CheckoutContainer>
  );

  const currentStage = isCompleted ? 'completed' : hasStarted ? 'form' : 'hero';

  return (
    <div className={styles.experience}>
      <AnimatePresence mode="wait" custom={animationDirection}>
        {currentStage === 'hero' && (
          <motion.section
            key="hero"
            custom={animationDirection}
            variants={stageVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className={`${styles.heroStage} ${onboardingHeroStyles.heroVisual}`}
          >
            <div className={onboardingHeroStyles.heroBackground}>
              <img
                className={onboardingHeroStyles.backgroundImage}
                src={heroBackgroundImage}
                alt="Consultoria Mariana Mósca"
              />
            </div>

            <div className={styles.heroLogo}>
              <Logo className={styles.heroLogoMark} />
            </div>

            <div className={styles.heroContent}>
              <h1 className={styles.heroTitle}>
                Seu corpo tem potencial para muito mais. Você está pronta para descobrir?
              </h1>

              <div className={styles.heroCopy}>
                <p>
                  Resultados não acontecem por acaso. Eles são construídos com método, estratégia e consistência.
                </p>
                <p>
                  A Consultoria Mariana Mósca foi desenvolvida para mulheres que querem transformar o corpo de verdade, com um treinamento individualizado que combina força, hipertrofia, fortalecimento do core e progressão planejada.
                </p>
                <p>Mais do que querer mudar, é preciso estar disposta a fazer acontecer.</p>
                <p>Por isso, antes de começar, quero conhecer você.</p>
                <p>
                  Preencha o formulário para que meu time avalie seus objetivos, sua disponibilidade e se o seu momento está alinhado com a proposta da consultoria.
                </p>
              </div>

              <div className={styles.heroActionsInline}>
                <span className={styles.timeBadge}>{HERO_TIME_BADGE_LABEL}</span>
                <Button
                  type="button"
                  size="app"
                  variant="primary"
                  iconRight="arrow_forward"
                  className={styles.heroButton}
                  onClick={handleStart}
                >
                  {HERO_CTA_LABEL}
                </Button>
              </div>
            </div>
          </motion.section>
        )}

        {currentStage === 'hero' && (
          <div className={styles.heroActionsFixed}>
            <span className={styles.timeBadge}>{HERO_TIME_BADGE_LABEL}</span>
            <Button
              type="button"
              size="app"
              variant="primary"
              iconRight="arrow_forward"
              className={styles.heroButton}
              onClick={handleStart}
            >
              {HERO_CTA_LABEL}
            </Button>
          </div>
        )}

        {currentStage === 'form' && (
          <motion.div
            key="form"
            custom={animationDirection}
            variants={stageVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className={styles.stage}
          >
            {renderForm()}
          </motion.div>
        )}

        {currentStage === 'completed' && (
          <motion.section
            key="completed"
            custom={animationDirection}
            variants={stageVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className={styles.confirmationStage}
          >
            <div className={styles.confirmationLogo}>
              <Logo />
            </div>
            <article className={`${pageStyles.checkoutCard} ${styles.confirmationCard}`}>
              <span className="material-symbols-outlined" aria-hidden="true">
                check_circle
              </span>
              <h1>Você já deu o primeiro passo!</h1>
              <p>
                Recebemos suas respostas.
              </p>
              <p>
                Minha equipe normalmente responde dentro de algumas horas.
              </p>
              <p className={styles.confirmationQueueJump}>
                Mas, se quiser agilizar seu atendimento, você pode furar a fila agora mesmo e falar com uma atendente do meu time.
              </p>
              <p>
                Ela vai entender seu objetivo, explicar como funciona a consultoria e verificar a disponibilidade para iniciarmos seu acompanhamento.
              </p>

              <Button
                type="button"
                size="app"
                variant="primary"
                className={styles.confirmationButton}
                onClick={handleWhatsAppClick}
              >
                Quero furar a fila →
              </Button>

              <p className={styles.socialProof}>
                Mais de 1000 mulheres já transformaram seus resultados treinando com o Método Mari Mósca. Agora pode ser a sua vez e vou adorar poder te ajudar.
              </p>
            </article>
          </motion.section>
        )}
      </AnimatePresence>

      <ConfirmModal
        open={showConfirmModal}
        title="Enviar minha resposta"
        message="Está tudo certo com os dados informados? Deseja enviar suas respostas?"
        confirmLabel="Enviar minha resposta"
        cancelLabel="Revisar"
        variant="info"
        icon="send"
        loading={isSubmitting}
        onConfirm={() => { void handleConfirmSubmit(); }}
        onCancel={() => {
          if (isSubmitting) {
            return;
          }

          setShowConfirmModal(false);
        }}
      />
    </div>
  );
};

export default LeadQualificationPage;
